import React from 'react';
import FirstRunHero from './FirstRunHero';
import JourneyPreview from './JourneyPreview';
import ExplainerVideo from './ExplainerVideo';
import NewRFQModal from '../../common/NewRFQModal';
import { useFirstRfqUpload } from '../../../hooks/useFirstRfqUpload';

/**
 * The zero-RFQ dashboard. Fluid like every other center panel — it shrinks
 * with the viewport, with a max inside so the hero copy keeps a readable
 * measure on a wide monitor.
 *
 * The mock also carries a market-feed strip, a free-text "just tell me" box
 * and a finished-RFQ demo. None of the three has anything behind it yet, so
 * they are left out rather than shipped as controls that do nothing; the
 * spacing below absorbs the gap instead of filler content.
 *
 * A dropped BOM opens the same naming popup the "New RFQ" action uses, seeded
 * from the file — so the first RFQ is still named before it exists.
 */

/**
 * The walkthrough section is hidden until the video actually exists (owner
 * call): a reserved "coming soon" frame is honest but it is still a third of
 * the first screen spent on nothing. Flip this back to `true` when the
 * recording lands and pass its `src` to `ExplainerVideo`; the component and
 * its empty state are kept precisely so that is a one-line change.
 */
const SHOW_WELCOME_VIDEO: boolean = false;

export interface FirstRunStateProps {
  /** Greeting name — the sign-in name's first word. */
  firstName: string;
}

export const FirstRunState: React.FC<FirstRunStateProps> = ({ firstName }) => {
  const { dropzone, openPicker, naming, suggestedName, confirmName, discard, starting, error } =
    useFirstRfqUpload();

  return (
    <div className="mx-auto flex w-full max-w-[900px] flex-col gap-[16px] pb-12">
      <FirstRunHero
        firstName={firstName}
        rootProps={dropzone.rootProps}
        inputProps={dropzone.inputProps}
        isDragActive={dropzone.isDragActive}
        starting={starting}
        error={error}
        onBrowse={openPicker}
      />
      <JourneyPreview />
      {SHOW_WELCOME_VIDEO && <ExplainerVideo />}
      <NewRFQModal
        open={naming}
        onClose={discard}
        onCreate={confirmName}
        defaultName={suggestedName}
      />
    </div>
  );
};

export default FirstRunState;
